import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  getDocs,
  query,
  where,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';

class UserDataService {
  constructor() {
    this.collections = {
      userSettings: 'userSettings',
      userPrompts: 'userPrompts', 
      userFeeds: 'userFeeds',
      userApiKeys: 'userApiKeys'
    };
  }

  // Helper method to get user document ID
  getUserDocId(userId) {
    return userId || 'anonymous';
  }

  // USER SETTINGS
  async getUserSettings(userId) {
    try {
      const docRef = doc(db, this.collections.userSettings, this.getUserDocId(userId));
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return docSnap.data();
      } else {
        // Return default settings for new users
        return this.getDefaultUserSettings();
      }
    } catch (error) {
      console.error('Error getting user settings:', error);
      return this.getDefaultUserSettings();
    }
  }

  async saveUserSettings(userId, settings) {
    try {
      const docRef = doc(db, this.collections.userSettings, this.getUserDocId(userId));
      await setDoc(docRef, {
        ...settings,
        lastUpdated: serverTimestamp(),
        userId: userId
      }, { merge: true });
      return true;
    } catch (error) {
      console.error('Error saving user settings:', error);
      return false;
    }
  }

  getDefaultUserSettings() {
    return {
      refreshInterval: 300, // 5 minutes
      theme: 'light',
      autoRefresh: true,
      showCompanyCards: true,
      finnhubEnabled: false, // FinHub disabled by default
      yahooFinanceEnabled: true // Yahoo Finance enabled by default
    };
  }

  // USER PROMPTS
  async getUserPrompts(userId) {
    try {
      const q = query(
        collection(db, this.collections.userPrompts),
        where('userId', '==', userId)
      );
      const querySnapshot = await getDocs(q);
      
      const prompts = [];
      querySnapshot.forEach((doc) => {
        prompts.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      // Always return user prompts, even if empty
      // Default prompts should only be shown when there are truly no user prompts
      return prompts.sort((a, b) => (a.order || 0) - (b.order || 0));
    } catch (error) {
      console.error('Error getting user prompts:', error);
      return this.getDefaultPrompts();
    }
  }

  async saveUserPrompt(userId, prompt) {
    try {
      const docRef = doc(collection(db, this.collections.userPrompts));
      await setDoc(docRef, {
        ...prompt,
        userId: userId,
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error saving user prompt:', error);
      return null;
    }
  }

  async updateUserPrompt(userId, promptId, updates) {
    try {
      const docRef = doc(db, this.collections.userPrompts, promptId);
      await updateDoc(docRef, {
        ...updates,
        lastUpdated: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error('Error updating user prompt:', error);
      return false;
    }
  }

  async deleteUserPrompt(userId, promptId) {
    try {
      const docRef = doc(db, this.collections.userPrompts, promptId);
      await deleteDoc(docRef);
      return true;
    } catch (error) {
      console.error('Error deleting user prompt:', error);
      return false;
    }
  }

  getDefaultPrompts() {
    return [
      {
        id: 'default-1',
        name: 'Risk Analysis',
        prompt: 'Analyze the risk factors for {COMPANY} ({SYMBOL}) based on this news. Consider market volatility, regulatory changes, competitive threats, and financial stability. Provide a risk assessment with specific concerns and potential impact on stock performance.',
        order: 1,
        isDefault: true
      },
      {
        id: 'default-2', 
        name: 'Market Impact',
        prompt: 'Evaluate the potential market impact of this news on {COMPANY} ({SYMBOL}). Consider short-term and long-term effects, sector implications, and investor sentiment. Provide specific price targets and timeline expectations.',
        order: 2,
        isDefault: true
      },
      {
        id: 'default-3',
        name: 'Investment Outlook',
        prompt: 'Provide an investment outlook for {COMPANY} ({SYMBOL}) based on this news. Analyze the fundamental strength, growth prospects, and valuation. Give a clear buy/hold/sell recommendation with reasoning and price targets.',
        order: 3,
        isDefault: true
      }
    ];
  }

  // USER FEEDS
  async getUserFeeds(userId) {
    try {
      const q = query(
        collection(db, this.collections.userFeeds),
        where('userId', '==', userId)
      );
      const querySnapshot = await getDocs(q);
      
      const feeds = [];
      querySnapshot.forEach((doc) => {
        feeds.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      // Always return user feeds, even if empty
      // Default feeds should only be shown when there are truly no user feeds
      return feeds.sort((a, b) => (a.order || 0) - (b.order || 0));
    } catch (error) {
      console.error('Error getting user feeds:', error);
      return this.getDefaultFeeds();
    }
  }

  async saveUserFeed(userId, feed) {
    try {
      const docRef = doc(collection(db, this.collections.userFeeds));
      await setDoc(docRef, {
        ...feed,
        userId: userId,
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error saving user feed:', error);
      return null;
    }
  }

  async updateUserFeed(userId, feedId, updates) {
    try {
      const docRef = doc(db, this.collections.userFeeds, feedId);
      await updateDoc(docRef, {
        ...updates,
        lastUpdated: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error('Error updating user feed:', error);
      return false;
    }
  }

  async deleteUserFeed(userId, feedId) {
    try {
      const docRef = doc(db, this.collections.userFeeds, feedId);
      await deleteDoc(docRef);
      return true;
    } catch (error) {
      console.error('Error deleting user feed:', error);
      return false;
    }
  }

  getDefaultFeeds() {
    return [
      {
        id: 'default-1',
        name: 'Yahoo Finance',
        url: 'https://finance.yahoo.com/news/rssindex',
        color: '#dc2626',
        enabled: true,
        order: 1,
        isDefault: true
      },
      {
        id: 'default-2',
        name: 'Bloomberg',
        url: 'https://news.google.com/rss/search?q=when:24h+allinurl:bloomberg.com&hl=en-US&gl=US&ceid=US:en',
        color: '#1e40af',
        enabled: true,
        order: 2,
        isDefault: true
      },
      {
        id: 'default-3',
        name: 'CNBC Business',
        url: 'https://search.cnbc.com/rs/search/combinedcms/view.xml?partnerId=wrss01&id=10001147',
        color: '#7c3aed',
        enabled: true,
        order: 3,
        isDefault: true
      }
    ];
  }

  // USER API KEYS (for non-MANAGED users only)
  async getUserApiKeys(userId) {
    try {
      const docRef = doc(db, this.collections.userApiKeys, this.getUserDocId(userId));
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return data;
      } else {
        return {
          openaiApiKey: '',
          finnhubApiKey: process.env.REACT_APP_FINNHUB_API_KEY || '', // Default FinHub key
          finnhubEnabled: false, // FinHub disabled by default
          yahooFinanceEnabled: true // Yahoo Finance enabled by default
        };
      }
    } catch (error) {
      console.error('Error getting user API keys:', error);
      return {
        openaiApiKey: '',
        finnhubApiKey: process.env.REACT_APP_FINNHUB_API_KEY || '',
        finnhubEnabled: false, // FinHub disabled by default
        yahooFinanceEnabled: true // Yahoo Finance enabled by default
      };
    }
  }

  async saveUserApiKeys(userId, apiKeys) {
    try {
      // Get current API keys to check existing state
      const currentApiKeys = await this.getUserApiKeys(userId);
      
      // Ensure only one finance module is enabled at a time
      if (apiKeys.yahooFinanceEnabled === true && currentApiKeys.finnhubEnabled === true) {
        // If enabling Yahoo Finance and FinHub is currently enabled, disable FinHub
        apiKeys.finnhubEnabled = false;
      } else if (apiKeys.finnhubEnabled === true && currentApiKeys.yahooFinanceEnabled === true) {
        // If enabling FinHub and Yahoo Finance is currently enabled, disable Yahoo Finance
        apiKeys.yahooFinanceEnabled = false;
      }

      const docRef = doc(db, this.collections.userApiKeys, this.getUserDocId(userId));
      await setDoc(docRef, {
        ...apiKeys,
        lastUpdated: serverTimestamp(),
        userId: userId
      }, { merge: true });
      return true;
    } catch (error) {
      console.error('Error saving user API keys:', error);
      return false;
    }
  }

  // Helper method to toggle finance modules with mutual exclusivity
  async toggleFinanceModule(userId, moduleName, enabled) {
    try {
      const currentApiKeys = await this.getUserApiKeys(userId);
      
      if (moduleName === 'finnhub') {
        currentApiKeys.finnhubEnabled = enabled;
        if (enabled) {
          currentApiKeys.yahooFinanceEnabled = false; // Disable Yahoo Finance
        }
      } else if (moduleName === 'yahooFinance') {
        currentApiKeys.yahooFinanceEnabled = enabled;
        if (enabled) {
          currentApiKeys.finnhubEnabled = false; // Disable FinHub
        }
      }
      
      return await this.saveUserApiKeys(userId, currentApiKeys);
    } catch (error) {
      console.error('Error toggling finance module:', error);
      return false;
    }
  }

  // MIGRATION HELPERS
  async migrateLocalStorageData(userId) {
    try {
      console.log('Starting localStorage migration for user:', userId);
      
      // Migrate prompts
      const savedPrompts = localStorage.getItem('bullbearly-custom-prompts');
      if (savedPrompts) {
        const prompts = JSON.parse(savedPrompts);
        if (prompts && prompts.length > 0) {
          for (const prompt of prompts) {
            if (!prompt.isDefault) {
              await this.saveUserPrompt(userId, prompt);
            }
          }
        }
        // Clear from localStorage after migration
        localStorage.removeItem('bullbearly-custom-prompts');
      }

      // Migrate feeds
      const savedFeeds = localStorage.getItem('bullbearly-rss-feeds');
      if (savedFeeds) {
        const feeds = JSON.parse(savedFeeds);
        if (feeds && feeds.length > 0) {
          for (const feed of feeds) {
            if (!feed.isDefault) {
              await this.saveUserFeed(userId, feed);
            }
          }
        }
        // Clear from localStorage after migration
        localStorage.removeItem('bullbearly-rss-feeds');
      }

      // Migrate API keys (only for non-MANAGED users)
      const savedApiKey = localStorage.getItem('bullbearly-openai-key');
      const savedFinnhubKey = localStorage.getItem('bullbearly-finnhub-key');
      const savedFinnhubEnabled = localStorage.getItem('bullbearly-finnhub-enabled');
      
      if (savedApiKey || savedFinnhubKey || savedFinnhubEnabled !== null) {
        const apiKeys = {
          openaiApiKey: savedApiKey || '',
          finnhubApiKey: savedFinnhubKey || process.env.REACT_APP_FINNHUB_API_KEY || '',
          finnhubEnabled: savedFinnhubEnabled !== null ? JSON.parse(savedFinnhubEnabled) : true
        };
        
        await this.saveUserApiKeys(userId, apiKeys);
        
        // Clear from localStorage after migration
        localStorage.removeItem('bullbearly-openai-key');
        localStorage.removeItem('bullbearly-finnhub-key');
        localStorage.removeItem('bullbearly-finnhub-enabled');
      }

      // Migrate settings
      const savedRefreshInterval = localStorage.getItem('bullbearly-refresh-interval');
      if (savedRefreshInterval) {
        const settings = {
          refreshInterval: parseInt(savedRefreshInterval)
        };
        await this.saveUserSettings(userId, settings);
        localStorage.removeItem('bullbearly-refresh-interval');
      }

      console.log('LocalStorage migration completed for user:', userId);
      return true;
    } catch (error) {
      console.error('Error during localStorage migration:', error);
      return false;
    }
  }

  // UTILITY METHODS
  async clearUserData(userId) {
    try {
      // This would be used for account deletion
      const collections = [
        this.collections.userSettings,
        this.collections.userPrompts,
        this.collections.userFeeds,
        this.collections.userApiKeys
      ];

      for (const collectionName of collections) {
        if (collectionName === this.collections.userSettings || collectionName === this.collections.userApiKeys) {
          // Single documents
          const docRef = doc(db, collectionName, this.getUserDocId(userId));
          await deleteDoc(docRef);
        } else {
          // Collections with multiple documents
          const q = query(
            collection(db, collectionName),
            where('userId', '==', userId)
          );
          const querySnapshot = await getDocs(q);
          
          const deletePromises = [];
          querySnapshot.forEach((docSnapshot) => {
            deletePromises.push(deleteDoc(docSnapshot.ref));
          });
          
          await Promise.all(deletePromises);
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error clearing user data:', error);
      return false;
    }
  }
}

const userDataService = new UserDataService();
export default userDataService;
